import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import InvoiceDocument from "./InvoiceDocument";

const HIDEABLE = [["delivery_date", "Datum isporuke"], ["due_date", "Datum dospijeća"], ["payment_type", "Način plaćanja"]];

function sampleInvoice(s, profile) {
  const p = profile || {};
  return {
    invoice_type: "racun", status: "issued", number: "1-P1-1", issue_date: new Date().toISOString().slice(0, 10), issue_time: "10:00",
    delivery_date: new Date().toISOString().slice(0, 10), due_date: new Date().toISOString().slice(0, 10), place_of_issue: p.city || "Rijeka",
    payment_type: "Transakcijski račun (Booking.com)", reservation_code: "LOD-2026-00001",
    client_name: "Marko Marković", client_address: "Ilica 1", client_postal_code: "10000", client_city: "Zagreb", client_country: "HR",
    items: [{ description: "Noćenje A-1 (10.08.2026. - 13.08.2026.)", quantity: 3, unit_price: 100, amount: 300, discount_percent: 20, price_after_discount: 80, vat_rate: 0, total: 240 }],
    subtotal: 300, discount_total: 60, vat_total: 0, total: 240, currency: "EUR",
    legal_text: p.pdv_exemption_text || "PDV nije obračunat temeljem čl. 90 stavak 2, Zakona o PDV-u",
    show_payment_info: true, payment_reference: "1-1-2026",
    theme_color: s.theme_color, header_text: s.header_text, footer_text: s.footer_text, hidden_fields: s.hidden_fields,
    supplier: { ...p, display_name: p.company_name || p.private_landlord_name || "Vaš naziv", logo_url: p.logo_url || s.logo_url, payment_model: p.payment_model || "HR99" },
  };
}

/** Fakturiranje → Izgled računa: tenant-wide look (colour, texts, hidden rows) + live A4 preview. */
export default function InvoiceLookTab({ profiles }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/invoice-settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-primary" /></div>;
  const update = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const { theme_color, logo_url, header_text, footer_text, hidden_fields } = s;
      const { data } = await api.put("/invoice-settings", { theme_color, logo_url, header_text, footer_text, hidden_fields });
      setS(data); toast.success("Izgled računa spremljen");
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
    finally { setSaving(false); }
  };
  const profile = profiles.find((p) => p.is_default) || profiles[0];
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]" data-testid="invoice-look-tab">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium">Boja računa</label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.theme_color || "#0f3d4c"} onChange={(e) => update({ theme_color: e.target.value })} className="h-9 w-16 cursor-pointer rounded border border-border" data-testid="cfg-theme-color" />
            <input value={s.theme_color || ""} onChange={(e) => update({ theme_color: e.target.value })} className="rw-input mono w-32" />
          </div>
        </div>
        <label className="block text-xs font-medium">Zamjenski logo (ako profil nema svoj)
          <input value={s.logo_url || ""} onChange={(e) => update({ logo_url: e.target.value })} placeholder="https://..." className="rw-input mt-1" data-testid="cfg-logo" />
        </label>
        <label className="block text-xs font-medium">Tekst ispod naslova računa
          <textarea rows={2} value={s.header_text || ""} onChange={(e) => update({ header_text: e.target.value })} className="rw-input mt-1" data-testid="cfg-header-text" />
        </label>
        <label className="block text-xs font-medium">Tekst u podnožju (ako profil nema svoju napomenu)
          <textarea rows={2} value={s.footer_text || ""} onChange={(e) => update({ footer_text: e.target.value })} className="rw-input mt-1" data-testid="cfg-footer-text" />
        </label>
        <div>
          <p className="mb-2 text-xs font-medium">Sakrij na računu</p>
          <div className="flex flex-wrap gap-2">
            {HIDEABLE.map(([k, l]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs hover:bg-muted">
                <input type="checkbox" checked={(s.hidden_fields || []).includes(k)} onChange={(e) => update({ hidden_fields: e.target.checked ? [...(s.hidden_fields || []), k] : (s.hidden_fields || []).filter((x) => x !== k) })} data-testid={`cfg-hide-${k}`} /> {l}
              </label>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">Promjene vrijede za nove račune. Podaci izdavatelja, IBAN, model i PDV upisuju se u profilu izdavatelja.</p>
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="invoice-look-save">
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />} Spremi izgled
        </button>
      </div>
      <div className="rounded-lg bg-slate-100 p-4 sm:p-6" data-testid="invoice-look-preview">
        <div className="mx-auto max-w-[820px] rounded-md bg-white p-6 shadow-sm sm:p-8"><InvoiceDocument inv={sampleInvoice(s, profile)} /></div>
      </div>
    </div>
  );
}
